"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { MagnifyingGlass, MapPin, CaretDown, X, Check } from "@phosphor-icons/react";

// ── Types ────────────────────────────────────────────────────

interface LocationResult {
  slug: string;
  name: string;
  state_code: string;
  status: string;
  camp_count: number;
}

// ── Constants ────────────────────────────────────────────────

const DEBOUNCE_MS = 300;
const RECENTS_KEY = "kamp_recent_locations";
const MAX_RECENTS = 3;

// ── Helpers ──────────────────────────────────────────────────

function loadRecents(): LocationResult[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    return raw ? (JSON.parse(raw) as LocationResult[]).slice(0, MAX_RECENTS) : [];
  } catch {
    return [];
  }
}

function saveRecent(loc: LocationResult) {
  try {
    const prev = loadRecents().filter((r) => r.slug !== loc.slug);
    localStorage.setItem(
      RECENTS_KEY,
      JSON.stringify([loc, ...prev].slice(0, MAX_RECENTS)),
    );
  } catch {
    // localStorage unavailable — degrade silently
  }
}

// ── Component ────────────────────────────────────────────────

export function LocationSearch({
  currentSlug,
  currentName,
  mode = "inline",
  placeholder = "Search cities...",
  onSelect,
  autoFocus = false,
}: {
  /** Current location slug (for highlighting) */
  currentSlug?: string;
  /** Current location display name */
  currentName?: string;
  /** "inline" = combobox in page flow. "modal" = full overlay. */
  mode?: "inline" | "modal";
  placeholder?: string;
  /** Override default navigation behavior */
  onSelect?: (loc: LocationResult) => void;
  autoFocus?: boolean;
}) {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<LocationResult[]>([]);
  const [recents] = useState<LocationResult[]>(loadRecents);
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Debounced search
  const search = useCallback((q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/location-search?q=${encodeURIComponent(q)}`);
        if (!res.ok) throw new Error("search failed");
        const data = await res.json();
        setResults(data.results ?? []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  }, []);

  // Geo nearest
  const handleNearMe = useCallback(async () => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    setOpen(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await fetch(
            `/api/location-search?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`,
          );
          if (!res.ok) throw new Error("geo search failed");
          const data = await res.json();
          setResults(data.results ?? []);
          setQuery("");
        } catch {
          setResults([]);
        } finally {
          setGeoLoading(false);
        }
      },
      () => {
        setGeoLoading(false);
      },
      { timeout: 5000 },
    );
  }, []);

  function handleSelect(loc: LocationResult) {
    saveRecent(loc);
    setOpen(false);
    setQuery("");
    // Set cookie
    document.cookie = `wildroost_location=${encodeURIComponent(loc.slug)};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`;
    if (onSelect) {
      onSelect(loc);
    } else if (loc.status === "live" || loc.status === "pre_launch") {
      router.push(`/${loc.slug}/camps`);
    } else {
      // coming_soon or not_live
      router.push(`/${loc.slug}`);
    }
  }

  // Keyboard navigation
  function handleKeyDown(e: React.KeyboardEvent) {
    const items = displayItems;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0 && activeIndex < items.length) {
      e.preventDefault();
      handleSelect(items[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  // What to show in the dropdown
  const displayItems: LocationResult[] =
    query.length >= 2
      ? results
      : recents.length > 0
        ? recents
        : [];

  const showDropdown = open && (displayItems.length > 0 || loading || geoLoading);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.children[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <div ref={containerRef} className={`relative ${mode === "modal" ? "w-full" : "w-full max-w-md"}`}>
      {/* Input row */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-wildroost-ink/30" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setActiveIndex(-1);
              search(e.target.value);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoFocus={autoFocus}
            autoComplete="off"
            className="w-full rounded-lg border border-wildroost-ink/15 bg-white py-2.5 pl-10 pr-3 text-sm text-wildroost-ink placeholder:text-wildroost-ink/40 focus:border-wildroost-teal focus:outline-none focus:ring-1 focus:ring-wildroost-teal/30"
            role="combobox"
            aria-expanded={showDropdown}
            aria-autocomplete="list"
            aria-controls="location-search-list"
          />
          {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-wildroost-teal/30 border-t-wildroost-teal" />
            </div>
          )}
        </div>

        {/* Near me button */}
        {"geolocation" in (typeof navigator !== "undefined" ? navigator : {}) && (
          <button
            type="button"
            onClick={handleNearMe}
            disabled={geoLoading}
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-wildroost-ink/15 bg-white px-3 py-2.5 text-sm text-wildroost-ink/60 transition hover:border-wildroost-teal hover:text-wildroost-teal disabled:opacity-50"
            aria-label="Find camps near me"
          >
            <MapPin size={16} aria-hidden="true" />
            {geoLoading ? "Locating..." : "Near me"}
          </button>
        )}
      </div>

      {/* Recents chips (shown when input empty and focused) */}
      {open && query.length < 2 && recents.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="text-[10px] font-medium uppercase tracking-wider text-wildroost-ink/40">Recent</span>
          {recents.map((loc) => (
            <button
              key={loc.slug}
              type="button"
              onClick={() => handleSelect(loc)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                loc.slug === currentSlug
                  ? "border-wildroost-teal bg-teal-bg text-wildroost-teal"
                  : "border-wildroost-ink/10 text-wildroost-ink/70 hover:border-wildroost-teal hover:text-wildroost-teal"
              }`}
            >
              {loc.name}, {loc.state_code}
            </button>
          ))}
        </div>
      )}

      {/* Results dropdown */}
      {showDropdown && (
        <ul
          id="location-search-list"
          ref={listRef}
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-auto rounded-lg border border-wildroost-ink/10 bg-white py-1 shadow-lg"
        >
          {geoLoading && (
            <li className="px-4 py-3 text-center text-sm text-wildroost-ink/50">
              <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-wildroost-teal/30 border-t-wildroost-teal" />{" "}
              Finding your location...
            </li>
          )}
          {!geoLoading && displayItems.length === 0 && query.length >= 2 && !loading && (
            <li className="px-4 py-3 text-center text-sm text-wildroost-ink/50">
              No cities found for &ldquo;{query}&rdquo;
            </li>
          )}
          {displayItems.map((loc, i) => (
            <li
              key={loc.slug}
              role="option"
              aria-selected={i === activeIndex}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => handleSelect(loc)}
              className={`flex cursor-pointer items-center justify-between px-4 py-2.5 text-sm transition ${
                i === activeIndex
                  ? "bg-wildroost-teal/5 text-wildroost-teal"
                  : loc.slug === currentSlug
                    ? "bg-wildroost-teal/3 font-medium text-wildroost-teal"
                    : "text-wildroost-ink hover:bg-wildroost-ink/3"
              }`}
            >
              <div className="flex items-center gap-2">
                {loc.slug === currentSlug && (
                  <Check size={14} weight="bold" className="shrink-0" aria-hidden="true" />
                )}
                <span className={loc.slug === currentSlug ? "" : "pl-[22px]"}>
                  {loc.name}, {loc.state_code}
                </span>
                {loc.status === "coming_soon" && (
                  <span className="rounded-full bg-wildroost-gold/10 px-2 py-0.5 text-[10px] font-bold text-wildroost-gold">
                    Coming Soon
                  </span>
                )}
              </div>
              {loc.camp_count > 0 && (
                <span className="text-xs text-wildroost-ink/40">
                  {loc.camp_count} {loc.camp_count === 1 ? "camp" : "camps"}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * LocationPill — compact trigger for the nav bar.
 * Shows current city name. Click opens a modal with LocationSearch.
 */
export function LocationPill({
  slug,
  name,
}: {
  slug: string;
  name: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Context selector, not a nav destination — visually deprioritized so it
          does not compete with the primary nav links (#1979). No button chrome
          (border/rounded-full/bg), subtle ink color that lifts to teal on hover. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 px-1.5 py-1 text-xs text-wildroost-ink/50 transition hover:text-wildroost-teal focus:outline-none focus-visible:ring-2 focus-visible:ring-wildroost-teal/40"
      >
        <MapPin size={12} aria-hidden="true" />
        <span className="max-w-[140px] truncate">{name}</span>
        <CaretDown size={10} weight="bold" aria-hidden="true" />
      </button>

      {/* Modal overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[400] flex items-start justify-center bg-wildroost-ink/40 pt-24 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-bold uppercase tracking-wider text-wildroost-ink/50">
                Change Location
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-wildroost-ink/40 hover:text-wildroost-ink"
                aria-label="Close"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <LocationSearch
              currentSlug={slug}
              currentName={name}
              mode="modal"
              placeholder="Search for a city..."
              autoFocus
              onSelect={() => setOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}
